/* eslint-disable @bigbinary/neeto/use-neetoui-classes */
import { Button, DropdownMenu, Tooltip } from "@bigbinary/neeto-atoms";
import classnames from "classnames";
import { Ellipsis, EllipsisVertical } from "lucide-react";
import { hyphenate } from "neetocist";
import PropTypes from "prop-types";
import { mergeRight } from "ramda";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_TOOLTIP_PROPS,
  MENU_ITEM_TYPES,
  SIZES,
  STYLE_TO_VARIANT,
} from "./constants";
import MenuItem from "./MenuItem";

const MoreDropdown = ({
  dropdownButtonProps = {},
  isVertical = false,
  isDisabled = false,
  dropdownProps = {},
  menuTopChildren,
  menuBottomChildren,
  menuItems = [],
}) => {
  const { t } = useTranslation();

  const {
    isRounded: isRoundedButton,
    // eslint-disable-next-line no-unused-vars
    size: _buttonSize = "medium",
    tooltipProps: customTooltipProps,
    style: buttonStyle,
    ...buttonProps
  } = dropdownButtonProps;

  const tooltipProps = mergeRight(DEFAULT_TOOLTIP_PROPS, customTooltipProps);
  const variant = STYLE_TO_VARIANT[buttonStyle] || "ghost";

  const TargetIcon = isVertical ? EllipsisVertical : Ellipsis;

  const isDropdownDisabled = isDisabled || dropdownProps.disabled;

  if (isDropdownDisabled && !tooltipProps.disabled) {
    return (
      <Tooltip
        content={tooltipProps.content}
        position={tooltipProps.position || "bottom"}
      >
        <span>
          <Button
            {...{ variant }}
            disabled
            data-dropdown-button-style="more-dropdown"
            data-testid="more-dropdown-button"
            icon={TargetIcon}
            size="icon"
            className={classnames("shrink-0", {
              "rounded-full": isRoundedButton,
            })}
            {...buttonProps}
          />
        </span>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu
      customTarget={
        <Button
          {...{ variant }}
          aria-haspopup="menu"
          aria-label={t("neetoMolecules.moreDropdown.buttonAriaLabel")}
          data-dropdown-button-style="more-dropdown"
          disabled={isDropdownDisabled}
          icon={TargetIcon}
          size="icon"
          className={classnames("shrink-0", {
            "rounded-full": isRoundedButton,
          })}
          {...buttonProps}
        />
      }
      dropdownProps={{
        "aria-label": t("neetoMolecules.moreDropdown.menuAriaLabel"),
      }}
      {...dropdownProps}
    >
      {menuTopChildren}
      {menuItems.map(({ isVisible = true, key, ...menuItemProps }) => {
        if (!isVisible) return null;

        return (
          <MenuItem
            key={key ?? hyphenate(menuItemProps.label || "menu-item")}
            {...menuItemProps}
          />
        );
      })}
      {menuBottomChildren}
    </DropdownMenu>
  );
};

MoreDropdown.propTypes = {
  dropdownButtonProps: PropTypes.shape({
    style: PropTypes.oneOf([
      "primary",
      "secondary",
      "tertiary",
      "danger",
      "danger_text",
      "text",
      "link",
    ]),
    size: PropTypes.oneOf(Object.values(SIZES)),
    iconSize: PropTypes.number,
    disabled: PropTypes.bool,
    isRounded: PropTypes.bool,
  }),
  isVertical: PropTypes.bool,
  isDisabled: PropTypes.bool,
  dropdownProps: PropTypes.object,
  menuTopChildren: PropTypes.node,
  menuBottomChildren: PropTypes.node,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      onClick: PropTypes.func,
      key: PropTypes.string,
      type: PropTypes.oneOf(Object.values(MENU_ITEM_TYPES)),
      isVisible: PropTypes.bool,
      hasSubItems: PropTypes.bool,
      dropdownProps: PropTypes.shape({
        position: PropTypes.string,
        trigger: PropTypes.string,
      }),
    })
  ),
};

export default MoreDropdown;
